import { PropsWithChildren, useEffect } from "react";
import { Box, Grid } from "@chakra-ui/react";
import { GetServerSideProps } from "next";
import { useRouter } from "next/router";
import { fetchNews } from "src/api/server/news";
import { INewsProps } from "src/api/server/news.types";
import { SimpleRichText } from "src/components/common/rich-text/simple-rich-text";
import { PAGE_OG_TYPE } from "src/components/layout/constants";
import { ArticleSection } from "src/components/pages/news/article-section";
import { CommentsSection } from "src/components/pages/news/comments-section";
import { AllTraderReactions } from "src/components/pages/news/components/all-trader-reactions/all-trader-reactions";
import { COMMENT_SECTION_ID, REACTION_SECTION_ID, LIKE_SECTION_ID } from "src/components/pages/news/constants";
import { NewsSidebar } from "src/components/pages/news/news-sidebar";
import { NewsTopSection } from "src/components/pages/news/news-top-section";
import { NewsUnderlyingSection } from "src/components/pages/news/news-underlying-section";
import { NEWS_DISCLAIMER_TEXT_TEST_ID } from "src/components/pages/news/news.test-ids";
import { ReactionSection } from "src/components/pages/news/reaction-section";
import { TrendingNewsArticlesSection } from "src/components/pages/news/trending-news-articles-section";
import { Container } from "src/components-mui/layout/container";
import { TOP_MARGIN_CONTENT_APP } from "src/consts/styles";
import { useContentPageVisitTracking } from "src/hooks/use-content-page-visit-tracking";
import { serverSideTranslations } from "src/i18n/server-side-translations";
import { useAuthModalStore } from "src/stores/auth-modal-store";
import { useUserStore } from "src/stores/user-store";
import { IBaseParam } from "src/types/common";
import { addServerTiming } from "src/utils/nextjs/add-server-timing";
import { useRouterCaller } from "src/utils/router/use-router-caller";

const Layout = (props: PropsWithChildren) => {
    const { isCallerContentApp } = useRouterCaller();
    return (
        <Container mt={isCallerContentApp ? TOP_MARGIN_CONTENT_APP : [7, 7, 10]} mb={[5, 10]}>
            <Grid
                templateAreas={[`"top" "article"`, `"top" "article"`, `"top empty" "article sidebar"`]}
                templateColumns={["auto", "auto", "minmax(0, 1fr) 336px", "minmax(0, 1fr) 358px"]}
                columnGap={[0, 0, 5, 7]}
            >
                {props.children}
            </Grid>
        </Container>
    );
};

const IsolateEffects = () => {
    const { setReturnUrl } = useAuthModalStore();

    useContentPageVisitTracking({});

    useEffect(() => {
        setReturnUrl(window.location.pathname);
    }, [setReturnUrl]);

    return null;
};

const TOP_NUMBER_OF_REACTIONS_DEFAULT = 50;

function Page({ data }: INewsProps) {
    const router = useRouter();
    const { user } = useUserStore();
    const entityId = router.query.slug as string;

    return (
        <>
            <IsolateEffects />
            <Layout>
                <NewsTopSection
                    gridArea="top"
                    mb={[0, 2]}
                    headline={data.headline}
                    tags={data.tags}
                    creationDate={data.creationDate ? new Date(data.creationDate) : undefined}
                    location={data.location}
                    numberOfReactions={data.numberOfReactions}
                    numberOfComments={data.numberOfComments}
                    numberOfLikes={data.likeInfo.count}
                    likeSectionId={LIKE_SECTION_ID}
                    commentSectionId={COMMENT_SECTION_ID}
                    reactionSectionId={REACTION_SECTION_ID}
                />
                <Box gridArea="article">
                    <ArticleSection
                        paragraphs={data.paragraphs}
                        headline={data.headline}
                        likeInfo={data.likeInfo}
                        likeSectionId={LIKE_SECTION_ID}
                        topTradersReactions={data.topTradersReactions}
                        underlyingsInTextData={data.underlyingsInTextData}
                        mb={[7, 7, 10]}
                        wordBreak="break-word"
                    />
                    {data.mentionedUnderlyingIsins.length > 0 && <NewsUnderlyingSection mb={[7, 7, 10]} isins={data.mentionedUnderlyingIsins} />}
                    {data.marketReactions && (
                        <ReactionSection
                            id={REACTION_SECTION_ID}
                            mb={[7, 7, 10]}
                            articleCreationDate={data.creationDate}
                            learnMoreAboutMarketReactionsTooltip={data.marketReactions.learnMoreAboutMarketReactionsTooltip}
                            reactions={data.marketReactions.reactionsDetails}
                            topNumberOfReactions={data.marketReactions.topNumberOfReactions || TOP_NUMBER_OF_REACTIONS_DEFAULT}
                            topTradersReactions={data.topTradersReactions}
                            AllTradersReactions={
                                <AllTraderReactions
                                    slug={entityId}
                                    reactions={data.marketReactions.reactionsDetails}
                                    hasAnyTraderAnsweredUnderlyingPricePredictionQuestion={
                                        data.marketReactions.hasAnyTraderAnsweredUnderlyingPricePredictionQuestion
                                    }
                                />
                            }
                        />
                    )}
                    <CommentsSection
                        id={COMMENT_SECTION_ID}
                        mb={[7, 7, 10]}
                        entityId={entityId}
                        user={user}
                        isCurrentUserQualifiedCommenter={data.isCurrentUserQualifiedCommenter}
                    />
                    <TrendingNewsArticlesSection slug={data.slug} moreNewsFeedLink={data.moreNewsFeedLink} mb={[7, 7, 10]} />
                    {data.copyrightAndDisclaimerText && (
                        <SimpleRichText
                            text={data.copyrightAndDisclaimerText}
                            fontSize="xs"
                            borderY="1px"
                            borderColor="gray.100"
                            py={2}
                            data-test-id={NEWS_DISCLAIMER_TEXT_TEST_ID}
                        />
                    )}
                </Box>

                <NewsSidebar
                    gridArea="sidebar"
                    display={["none", "none", "block"]}
                    magazineTeaser={data.magazineTeaser}
                    recommendations={data.recommendations}
                />
            </Layout>
        </>
    );
}

type IParam = IBaseParam & {
    slug: string;
};

export const getServerSideProps: GetServerSideProps<INewsProps, IParam> = async context => {
    const { slug } = context.params!;

    const [props, translations] = await addServerTiming(context.res, "ssprops", async () => {
        const props = await fetchNews(slug, context);
        const translations = await serverSideTranslations(context.locale as string, [
            "news",
            "common",
            "comment",
            "reactions",
            "feed",
            "voting-module",
        ]);

        return [props, translations];
    });

    if (!props) {
        return { notFound: true };
    }

    return {
        props: {
            ...props,
            data: {
                ...props.data,
                slug: slug,
            },
            head: { ...props.head, ogTitle: props.head.title, ogType: PAGE_OG_TYPE.article },
            ...translations,
        },
    };
};

export default Page;
